import React, { useState, useEffect } from 'react';
import { Button, Spinner } from '@fluentui/react-components';
import { useAuth } from '@/services/auth';
import { api } from '@/services/api';
import { AxiosError } from 'axios';

interface AuthorizationCheckProps {
  children: React.ReactNode;
}

/**
 * Component that checks if authenticated user is authorized (in allowlist)
 * Shows access denied page if user gets 403 from API
 */
export const AuthorizationCheck: React.FC<AuthorizationCheckProps> = ({ children }) => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const { user, logout } = useAuth();

  useEffect(() => {
    checkAuthorization();
  }, []);

  const checkAuthorization = async () => {
    try {
      // Make a simple API call to check if user is authorized
      // Using settings endpoint as it's lightweight
      await api.settings.get();
      setIsAuthorized(true);
    } catch (error) {
      // Check if it's a 403 (forbidden) error
      if ((error as AxiosError).response?.status === 403) {
        setIsAuthorized(false);
      } else {
        // For other errors, assume authorized (network issues, etc.)
        // The actual API calls will handle real errors
        setIsAuthorized(true);
      }
    } finally {
      setIsChecking(false);
    }
  };

  // Show loading while checking
  if (isChecking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
        <Spinner label="Checking authorization..." />
      </div>
    );
  }

  // Show access denied if not authorized
  if (isAuthorized === false) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
      }}>
        <h1 style={{ marginBottom: '20px' }}>Access Denied</h1>

        <div style={{
          maxWidth: '500px',
          marginBottom: '30px',
          lineHeight: '1.6',
        }}>
          <p style={{ marginBottom: '15px' }}>
            Welcome to <strong>Enterprise CortexFlow</strong>
          </p>

          <p style={{ marginBottom: '15px' }}>
            Your account <strong>{user?.email || 'Unknown'}</strong> has been
            authenticated but is not yet authorized to access this application.
          </p>

          <p style={{ marginBottom: '15px' }}>
            Please contact your administrator to request access to the system.
          </p>
        </div>

        <Button
          appearance="primary"
          size="large"
          onClick={() => logout()}
        >
          Sign Out
        </Button>
      </div>
    );
  }

  // User is authorized, render the app
  return <>{children}</>;
};
